import { useRef, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Button,
  useToast,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Tooltip,
  Flex,
} from '@chakra-ui/react';

import { DeleteIcon } from '@chakra-ui/icons';

import { apiExcludeCallListItem } from 'api';

import type { APICallListData } from 'types/api.types';

const ItemIgnore = ({
  item,
  queryKey,
  onAction,
}: {
  item: APICallListData;
  queryKey: unknown[];
  onAction?: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const queryClient = useQueryClient();

  const onClose = () => setIsOpen(false);

  const ignoreMutation = useMutation({
    mutationFn: () => {
      return apiExcludeCallListItem(item.id);
    },
    onSuccess: () => {
      // Update the cache to remove the item
      queryClient.setQueryData(queryKey, (oldData: any) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          results: oldData.results.filter(
            (callItem: APICallListData) => callItem.id !== item.id,
          ),
          count: oldData.count - 1,
        };
      });

      toast({
        title: 'Item removed successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      if (onAction) onAction();

      onClose();
    },
    onError: (error: any) => {
      let errorMessage = 'Unknown error occurred';

      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.detail) {
        errorMessage = error.detail;
      }

      toast({
        title: 'Error removing item',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });

      onClose();
    },
  });

  return (
    <Flex justify="right" width="100%">
      <Tooltip label="Remove from list" hasArrow placement="top">
        <IconButton
          aria-label="Remove from list"
          icon={<DeleteIcon />}
          size="sm"
          variant="outline"
          onClick={() => setIsOpen(true)}
          borderColor="gray.300"
          _hover={{
            backgroundColor: 'red.100',
            color: 'red.600',
            border: '1px solid',
          }}
        />
      </Tooltip>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remove Item
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to remove <b>{item.name}</b> from the call
              list?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={() => ignoreMutation.mutate()}
                isLoading={ignoreMutation.isPending}
                ml={3}
              >
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  );
};

export default ItemIgnore;
